import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChevronLeft, Save, Eye, EyeOff, Loader2, User, Mail, Building2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const StaffAccount = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Account info (read-only)
  const [accountInfo, setAccountInfo] = useState(null);
  
  // Editable fields
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Track changes
  const [emailChanged, setEmailChanged] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);

  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const response = await api.get('/auth/staff-account');
        setAccountInfo(response.data);
        setEmail(response.data.email || "");
      } catch (error) {
        toast.error("Failed to load account info");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccountInfo();
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailChanged(e.target.value !== (accountInfo?.email || ""));
  };

  const handleSaveEmail = async () => {
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/auth/staff-account', { email });
      toast.success("Email updated successfully");
      setAccountInfo(prev => ({ ...prev, email }));
      setEmailChanged(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/auth/staff-account', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordChanging(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/staff")}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">My Account</h1>
          <p className="text-sm text-muted-foreground">Manage your account settings</p>
        </div>
      </div>

      {/* Account Info (Read-only) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4" />
            Account Information
          </CardTitle>
          <CardDescription>Your profile details from your company</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <p className="font-medium">{accountInfo?.name}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Employee ID</Label>
              <p className="font-medium font-mono">{accountInfo?.employee_number}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Company
              </Label>
              <p className="font-medium">{accountInfo?.company_name}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Role
              </Label>
              <p className="font-medium capitalize">{accountInfo?.role || accountInfo?.user_role}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address
          </CardTitle>
          <CardDescription>Used for login and notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              className="max-w-md"
              data-testid="account-email-input"
            />
          </div>
          {emailChanged && (
            <Button
              onClick={handleSaveEmail}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="save-email-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Email</>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Change Password</CardTitle>
          <CardDescription>Update your login password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passwordChanging ? (
            <Button
              variant="outline"
              onClick={() => setPasswordChanging(true)}
              data-testid="change-password-btn"
            >
              Change Password
            </Button>
          ) : (
            <div className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min. 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="save-password-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Update Password</>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPasswordChanging(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffAccount;
